import { useEffect, useRef } from 'react';

/** How often a badge re-checks the server while the tab is in front. */
export const LIVE_REFRESH_MS = 60000;

/** Run `callback` now, on an interval, and whenever the tab comes back to the front. A hidden tab
 *  is not polled, or a machine back from sleep fires a burst; `callback` is held in a ref. */
export default function useLiveRefresh(callback, { enabled = true, intervalMs = LIVE_REFRESH_MS } = {}) {
    const cbRef = useRef(callback);
    cbRef.current = callback;

    useEffect(() => {
        if (!enabled) return undefined;

        const run = () => { if (!document.hidden) cbRef.current?.(); };
        run();

        const timer = setInterval(run, intervalMs);
        const onVisible = () => { if (!document.hidden) cbRef.current?.(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);

        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [enabled, intervalMs]);
}
